/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactSlider from 'react-slider';

function Handle(props) {
  const {
    pp,
    value,
    handleStyle,
    handleTextContainerStyle,
    handleSize,
    handleTextStyle,
  } = props;

  return (
    <>
      <div {...pp} style={{ ...pp.style, boxSizing: 'content-box', ...handleStyle }} />
      <div style={{
        ...pp.style,
        ...handleTextContainerStyle,
      }}
      >
        <div style={{
          marginLeft: handleSize, width: '100%', whiteSpace: 'nowrap', ...handleTextStyle,
        }}
        >
          {value}
        </div>
      </div>
    </>
  );
}

Handle.propTypes = {
  pp: PropTypes.shape({
    style: PropTypes.shape({}),
  }).isRequired,
  value: PropTypes.string,
  handleStyle: PropTypes.shape({}).isRequired,
  handleTextContainerStyle: PropTypes.shape({}).isRequired,
  handleSize: PropTypes.number.isRequired,
  handleTextStyle: PropTypes.shape({}).isRequired,
};

Handle.defaultProps = {
  value: '',
};

/**
 * Slider for selecting a range of values
 */
export default function Slider(props) {
  const {
    min,
    max,
    finishSliding,
    mainColor,
    backColor,
    style,
  } = props;

  const [range, setRange] = useState([min, max]);

  const trackHeight = style.trackStyle?.height || 4;
  const handleSize = style.handleStyle?.height || trackHeight * 3;

  // The Main rail
  const trackStyle = {
    ...style.trackStyle,
    top: handleSize / 2,
  };
  if (!trackStyle.backgroundColor) {trackStyle.backgroundColor = backColor;}
  if (!trackStyle.height) {trackStyle.height = trackHeight;}

  // The overlay on top of the main rail
  const railStyle = {
    ...style.railStyle,
    top: handleSize / 2,
  };
  if (!railStyle.height) {railStyle.height = trackStyle.height;}
  if (!railStyle.backgroundColor) {railStyle.backgroundColor = mainColor;}

  const handleStyle = {
    ...style.handleStyle,
    width: handleSize,
    height: handleSize,
    borderWidth: trackHeight / 2,
  };
  if (!handleStyle.borderColor) {handleStyle.borderColor = mainColor;}
  if (!handleStyle.borderStyle) {handleStyle.borderStyle = 'solid';}
  if (!handleStyle.borderRadius) {handleStyle.borderRadius = '100%';}
  if (!handleStyle.cursor) {handleStyle.cursor = 'pointer';}
  if (!handleStyle.outline) {handleStyle.outline = 'none';}
  if (!handleStyle.backgroundColor) {handleStyle.backgroundColor = '#fff';}

  const handleTextContainerStyle = {
    top: handleSize + 6,
    transform: 'translateX(-50%)',
  };

  return (
    <div className="snapgadget-price-slider" style={style.container}>
      <ReactSlider
        value={range}
        min={min}
        max={max}
        marks={[min, max]}
        pearling
        onChange={(value) => setRange(value)}
        onAfterChange={(value) => {
          setRange(value);
          finishSliding({
            min: value[0],
            max: value[1],
          });
        }}
        className="snapgadget-slider"
        thumbClassName="snapgadget-slider-thumb"
        markClassName="snapgadget-slider-mark"
        thumbActiveClassName="snapgadget-slider-thumb-active"
        trackClassName="snapgadget-slider-track"
        renderThumb={(pp, state) => (
          <Handle
            key={pp.key}
            pp={pp}
            value={`$ ${state.valueNow}`}
            handleStyle={handleStyle}
            handleTextContainerStyle={handleTextContainerStyle}
            handleTextStyle={{
              color: style.label.color || mainColor,
              fontSize: style.label.fontSize,
            }}
            handleSize={handleSize}
          />
        )}
        renderTrack={(pp) => {
          if (pp.key === 'snapgadget-slider-track-1') {
            return <div {...pp} style={{ ...pp.style, ...railStyle }} />;
          }
          return <div {...pp} style={{ ...pp.style, ...trackStyle }} />;
        }}
        renderMark={(pp) => (
          <Handle
            key={pp.key}
            pp={pp}
            value={`$ ${pp.key}`}
            handleStyle={{
              ...handleStyle,
              borderColor: trackStyle.backgroundColor,
            }}
            handleTextContainerStyle={handleTextContainerStyle}
            handleTextStyle={{
              color: trackStyle.backgroundColor,
              fontSize: style.label.fontSize,
            }}
            handleSize={handleSize}
          />
        )}
      />
    </div>
  );
}

Slider.propTypes = {
  /**
   * Minimum value for the slider
   */
  min: PropTypes.number,
  /**
   * Maximum value for the slider
   */
  max: PropTypes.number,
  /**
   * Function that will be called when the slider values are changed
   */
  finishSliding: PropTypes.func.isRequired,
  /**
   * Color used for foreground including active handles and rail
   */
  mainColor: PropTypes.string,
  /**
   * Color used for the background including stationary handles and
   * background rail color.
   */
  backColor: PropTypes.string,
  /**
  * An object containing styles to applied to the containing div. Set
  * mainColor and backColor to null if specifying specific styles.
  */
  style: PropTypes.shape({
    label: PropTypes.shape({
      color: PropTypes.string,
      fontSize: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    container: PropTypes.shape({
      marginTop: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    railStyle: PropTypes.shape({
      backgroundColor: PropTypes.string,
    }),
    trackStyle: PropTypes.shape({
      height: PropTypes.number,
      backgroundColor: PropTypes.string,
    }),
    handleStyle: PropTypes.shape({
      borderColor: PropTypes.string,
      borderStyle: PropTypes.string,
      borderRadius: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      cursor: PropTypes.string,
      outline: PropTypes.string,
      backgroundColor: PropTypes.string,
      height: PropTypes.number,
    }),
  }),
};

Slider.defaultProps = {
  min: 0,
  max: 10000,
  mainColor: '#7f8c8d', // asbestos
  backColor: '#ecf0f1', // clouds
  style: {
    label: {
      fontSize: '1em',
      color: null,
    },
    container: {},
    railStyle: {},
    trackStyle: {
      height: 4,
    },
    handleStyle: {
      borderStyle: 'solid',
      borderRadius: '100%',
      cursor: 'pointer',
      outline: 'none',
      backgroundColor: '#fff',
    },
  },
};
